import { useRef, type ReactNode, type CSSProperties } from "react";
import { Link } from "react-router-dom";

interface MagneticTiltProps {
  children: ReactNode;
  className?: string;
  /** Max rotation in degrees */
  maxTilt?: number;
  /** Z-lift on hover, px */
  lift?: number;
  href?: string;
  onClick?: () => void;
  style?: CSSProperties;
}

/**
 * Wraps its children in a 3D-perspective container that rotates subtly
 * toward the mouse position, with a soft gold glow following the cursor.
 * Falls back to a static div on touch devices.
 */
export default function MagneticTilt({
  children,
  className = "",
  maxTilt = 6,
  lift = 6,
  href,
  onClick,
  style,
}: MagneticTiltProps) {
  const ref = useRef<HTMLAnchorElement | HTMLDivElement | null>(null);

  const handleMove = (e: React.MouseEvent) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const px = (x / rect.width) * 2 - 1; // -1..1
    const py = (y / rect.height) * 2 - 1;
    el.style.setProperty("--tiltX", `${(-py * maxTilt).toFixed(2)}deg`);
    el.style.setProperty("--tiltY", `${(px * maxTilt).toFixed(2)}deg`);
    el.style.setProperty("--liftZ", `${lift}px`);
    el.style.setProperty("--mx", `${(x / rect.width) * 100}%`);
    el.style.setProperty("--my", `${(y / rect.height) * 100}%`);
  };

  const handleLeave = () => {
    const el = ref.current;
    if (!el) return;
    el.style.setProperty("--tiltX", "0deg");
    el.style.setProperty("--tiltY", "0deg");
    el.style.setProperty("--liftZ", "0px");
  };

  const sharedProps = {
    ref: ref as React.RefObject<HTMLAnchorElement & HTMLDivElement>,
    className: `lum-tilt ${className}`,
    onMouseMove: handleMove,
    onMouseLeave: handleLeave,
    style,
  };

  if (href) {
    // Internal route — use Link so we don't blow away the SPA state
    if (href.startsWith("/")) {
      return (
        <Link {...sharedProps} to={href} onClick={onClick}>
          {children}
        </Link>
      );
    }
    return (
      <a {...sharedProps} href={href} onClick={onClick}>
        {children}
      </a>
    );
  }
  return (
    <div {...sharedProps} onClick={onClick}>
      {children}
    </div>
  );
}